import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Select, Space, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CompanyAdminGate } from './CompanyAdminGate';
import {
  companyAdminError,
  linkUserByEmail,
  loadCompanyRoster,
  updateCompanyMembershipRole,
  updateMemberProfile,
  type CompanyRosterRow,
} from './companyAdminApi';
import { COMPANY_MANAGER_ROLES } from '../AppShell/companyAdminAccess';
import { apiClient } from '../../services/apiClient';
import { ProfileDataEditor } from '../UserManagement/ProfileDataEditor';
import { nestProfilePreferences, normalizeProfileData } from '../../utils/normalizeProfileData';

const { Title, Text } = Typography;

const ROLE_OPTIONS = [
  { value: 'member', label: 'Member' },
  { value: 'admin', label: 'Admin' },
  { value: 'owner', label: 'Owner' },
  ...COMPANY_MANAGER_ROLES.filter((role) => role === 'company_admin').map((role) => ({
    value: role,
    label: 'Company admin',
  })),
];

const UsersBody: React.FC<{
  companyId: string;
  companyName: string;
  instanceId: string | null;
}> = ({ companyId, companyName, instanceId }) => {
  const [rows, setRows] = useState<CompanyRosterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [email, setEmail] = useState('');
  const [linkRole, setLinkRole] = useState('member');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [editing, setEditing] = useState<CompanyRosterRow | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm] = Form.useForm();

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await loadCompanyRoster(companyId));
    } catch (err) {
      setRows([]);
      setError(companyAdminError(err, 'Could not load the company roster.'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) => {
      const hay = [row.full_name, row.username, row.email, row.role].join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [query, rows]);

  const changeRole = async (row: CompanyRosterRow, role: string) => {
    setError(null);
    try {
      await updateCompanyMembershipRole(row.id, role);
      await refresh();
    } catch (err) {
      setError(companyAdminError(err, 'Could not update the membership role.'));
    }
  };

  const linkUser = async () => {
    setLinkError(null);
    if (!instanceId) {
      setLinkError('The selected company has no membership id for this session, so a user cannot be linked from here.');
      return;
    }
    if (!email.trim()) {
      setLinkError('Enter the email of an existing user.');
      return;
    }
    setLinking(true);
    try {
      await linkUserByEmail(instanceId, email, linkRole);
      setEmail('');
      await refresh();
    } catch (err) {
      setLinkError(companyAdminError(err, 'Could not link that user to the company.'));
    } finally {
      setLinking(false);
    }
  };

  const openProfile = async (row: CompanyRosterRow) => {
    setEditing(row);
    setError(null);
    profileForm.setFieldsValue({
      username: row.username,
      email: row.email,
      full_name: row.full_name,
      is_active: row.is_active !== false,
      profile_data: undefined,
    });
    try {
      const response = await apiClient.get<{
        username?: string;
        email?: string;
        full_name?: string;
        profile_data?: unknown;
      }>(`/users/${row.user_id}`);
      profileForm.setFieldsValue({
        username: response.data.username || row.username,
        email: response.data.email || row.email,
        full_name: response.data.full_name || row.full_name,
        profile_data: normalizeProfileData(response.data.profile_data),
      });
    } catch (err) {
      setError(companyAdminError(err, 'Could not load profile details. Roster fields can still be saved.'));
    }
  };

  const saveProfile = async () => {
    if (!editing) return;
    setSavingProfile(true);
    setError(null);
    try {
      const values = await profileForm.validateFields();
      const body: {
        username: string;
        email: string;
        full_name?: string;
        is_active: boolean;
        profile_data?: Record<string, unknown>;
      } = {
        username: values.username,
        email: values.email,
        full_name: values.full_name,
        is_active: values.is_active,
      };
      if (values.profile_data !== undefined) {
        body.profile_data = nestProfilePreferences(normalizeProfileData(values.profile_data));
      }
      await updateMemberProfile(editing.id, body);
      setEditing(null);
      await refresh();
    } catch (err) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      setError(companyAdminError(err, 'Could not save the member profile.'));
    } finally {
      setSavingProfile(false);
    }
  };

  const columns: ColumnsType<CompanyRosterRow> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, row) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => void openProfile(row)}>
          {row.full_name || row.username || row.user_id}
        </Button>
      ),
    },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (value) => value || '—' },
    {
      title: 'Membership role',
      dataIndex: 'role',
      key: 'role',
      render: (value, row) => (
        <Select
          size="small"
          style={{ minWidth: 140 }}
          value={String(value || 'member').toLowerCase()}
          options={ROLE_OPTIONS}
          onChange={(next) => void changeRole(row, next)}
        />
      ),
    },
    {
      title: 'Membership',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (value) => (value === false ? 'Inactive' : 'Active'),
    },
  ];

  return (
    <Card>
      <Title level={3}>Users</Title>
      <Text type="secondary">Roster for {companyName}. This list is that company only.</Text>
      <Space direction="vertical" style={{ width: '100%', marginTop: 16 }} size="middle">
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <Input
          allowClear
          placeholder="Filter this roster by name or email"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Form layout="inline">
          <Form.Item label="Link existing user">
            <Input
              placeholder="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Form.Item>
          <Form.Item label="Role">
            <Select style={{ width: 140 }} value={linkRole} options={ROLE_OPTIONS} onChange={setLinkRole} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" loading={linking} onClick={() => void linkUser()}>
              Link
            </Button>
          </Form.Item>
        </Form>
        {linkError ? <Alert type="error" showIcon message={linkError} /> : null}
        <Text type="secondary">
          Linking uses an existing account email. This page does not create a password. Password setup is on Platform administration → Users.
        </Text>
        {editing ? (
          <Card size="small" title={`Profile for ${editing.full_name || editing.email || editing.user_id}`}>
            <Form form={profileForm} layout="vertical">
              <Form.Item name="full_name" label="Full name">
                <Input />
              </Form.Item>
              <Form.Item name="username" label="Username" rules={[{ required: true, message: 'Username is required' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="is_active" label="Membership active" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
              <Form.Item name="profile_data" label="Profile data">
                <ProfileDataEditor />
              </Form.Item>
              <Space>
                <Button type="primary" loading={savingProfile} onClick={() => void saveProfile()}>
                  Save profile
                </Button>
                <Button onClick={() => setEditing(null)}>Cancel</Button>
              </Space>
            </Form>
            <Text type="secondary">
              Save writes the account name, email, username, profile data, and this company membership active flag. It does not change the platform account role.
            </Text>
          </Card>
        ) : null}
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={visible}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: loading ? 'Loading' : 'No members match this company.' }}
        />
      </Space>
    </Card>
  );
};

export const CompanyAdminUsersPage: React.FC = () => (
  <CompanyAdminGate>
    {(target) => (
      <UsersBody
        companyId={target.companyId}
        companyName={target.companyName}
        instanceId={target.instanceId}
      />
    )}
  </CompanyAdminGate>
);

export default CompanyAdminUsersPage;
